import React, { useState } from 'react';
import { View, ScrollView, TouchableOpacity, Alert, ActivityIndicator } from 'react-native';
import { Text } from '@/components';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ArrowLeft, Bell, Bookmark, Star, Footprints, Trophy, MessageCircle } from 'lucide-react-native';
import { useRouter } from 'expo-router';
import { API_URL } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import styles from '../settings.styles';
import permStyles from './permissions.styles';
import theme from '@/constants/colors';
import { useTranslation } from 'react-i18next';

interface TestNotification {
  type: string;
  title: string;
  body: string;
  icon: React.ReactNode;
  color: string;
}

const getTestNotifications = (t: (key: string) => string): TestNotification[] => [
  {
    type: 'trail_save',
    title: t('testNotifications.trailSaved'),
    body: t('testNotifications.trailSavedBody'),
    icon: <Bookmark size={22} color={theme.accentSecondary} />,
    color: theme.accentSecondary,
  },
  {
    type: 'trail_follow',
    title: t('testNotifications.trailFollowed'),
    body: t('testNotifications.trailFollowedBody'),
    icon: <Footprints size={22} color={theme.accentPrimary} />,
    color: theme.accentPrimary,
  },
  {
    type: 'trail_comment',
    title: t('testNotifications.newReview'),
    body: t('testNotifications.newReviewBody'),
    icon: <Star size={22} color={theme.accentSecondary} />,
    color: theme.accentSecondary,
  },
  {
    type: 'achievement',
    title: t('testNotifications.achievementUnlocked'),
    body: t('testNotifications.achievementUnlockedBody'),
    icon: <Trophy size={22} color="#FFD700" />,
    color: '#FFD700',
  },
  {
    type: 'system',
    title: t('testNotifications.welcome'),
    body: t('testNotifications.welcomeBody'),
    icon: <Bell size={22} color={theme.textSecondary} />,
    color: theme.textSecondary,
  },
];

export default function NotificationDebugScreen() {
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const refreshNotifications = () => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
  const { t } = useTranslation();
  const [sending, setSending] = useState<string | null>(null);

  const sendTestNotification = async (notif: TestNotification) => {
    if (!auth?.session?.accessToken) {
      Alert.alert(t('common.error'), t('testNotifications.mustBeLoggedIn'));
      return;
    }

    setSending(notif.type);
    try {
      // We use the notification API to create a notification directly.
      // Since there's no "send to self" endpoint, we'll use a small trick:
      // POST to the push-token endpoint is already used for registration,
      // so we'll create a lightweight admin endpoint call — or simply create
      // the notification locally via the context.
      //
      // For testing, we add it directly to the local state so the UI
      // updates immediately, and ALSO hit a simple API to persist it.
      //
      // The simplest approach: hit a test endpoint that creates a notification
      // for the current user.

      const res = await fetch(`${API_URL}/notifications/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.session.accessToken}`,
        },
        body: JSON.stringify({
          type: notif.type,
          title: notif.title,
          body: notif.body,
        }),
      });

      if (res.ok) {
        Alert.alert(t('testNotifications.sent'), t('testNotifications.sentMessage', { title: notif.title }));
        refreshNotifications();
      } else {
        // If the test endpoint doesn't exist yet, fall back to
        // just adding it locally so we can still test the UI.
        const err = await res.json().catch(() => ({}));
        if (res.status === 404) {
          Alert.alert(
            t('testNotifications.backendNotUpdated'),
            t('testNotifications.backendNotUpdatedMessage'),
          );
        } else {
          Alert.alert(t('common.error'), err.detail || t('testNotifications.failedToSend'));
        }
      }
    } catch (err: any) {
      Alert.alert(t('common.error'), err?.message || t('testNotifications.failedToSend'));
    } finally {
      setSending(null);
    }
  };

  const clearAll = async () => {
    // Just refresh from server — the UI will update
    refreshNotifications();
    Alert.alert(t('testNotifications.refreshed'), t('testNotifications.refreshedMessage'));
  };

  return (
    <View style={[permStyles.container, { paddingTop: insets.top }]}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => router.back()}>
          <ArrowLeft size={20} color={theme.accentPrimary} />
        </TouchableOpacity>
        <Text style={styles.title}>{t('testNotifications.title')}</Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView contentContainerStyle={permStyles.content}>
        <View style={permStyles.summaryCard}>
          <View style={permStyles.summaryIconRow}>
            <MessageCircle size={28} color={theme.accentPrimary} />
            <Text style={permStyles.summaryTitle}>{t('testNotifications.debugger')}</Text>
          </View>
          <Text style={permStyles.summaryBody}>
            {t('testNotifications.debuggerDescription')}
          </Text>
        </View>

        {getTestNotifications(t).map((notif) => (
          <View key={notif.type} style={permStyles.permCard}>
            <View style={permStyles.permRow}>
              <View style={[permStyles.permIconContainer, { backgroundColor: notif.color + '22' }]}>
                {notif.icon}
              </View>
              <View style={permStyles.permInfo}>
                <Text style={permStyles.permLabel}>{notif.title}</Text>
                <Text style={permStyles.permDescription}>{notif.body}</Text>
              </View>
            </View>
            <TouchableOpacity
              style={[permStyles.enableButton, sending === notif.type && { opacity: 0.6 }]}
              onPress={() => sendTestNotification(notif)}
              disabled={sending === notif.type}
            >
              {sending === notif.type ? (
                <ActivityIndicator size="small" color={theme.backgroundPrimary} />
              ) : (
                <Text style={permStyles.enableButtonText}>{t('testNotifications.sendTest')}</Text>
              )}
            </TouchableOpacity>
          </View>
        ))}

        <TouchableOpacity style={permStyles.systemSettingsButton} onPress={clearAll}>
          <Text style={permStyles.systemSettingsButtonText}>{t('testNotifications.refreshList')}</Text>
        </TouchableOpacity>

        <Text style={permStyles.noteText}>
          {t('testNotifications.tip')}
        </Text>
      </ScrollView>
    </View>
  );
}